import {
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import {
  MAT_BOTTOM_SHEET_DATA,
  MatBottomSheetRef,
} from '@angular/material/bottom-sheet'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatDividerModule } from '@angular/material/divider'
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar'
import { firstValueFrom } from 'rxjs'

import { MissingProductsSummary } from '../../../../../types/reports'
import { ReportService } from '../../../../../services/report.service'
import { TranslationService } from '../../../../../services/translation.service'

export type MissingProductsSheetData = {
  reportId: string
  year: number | null
}

@Component({
  selector: 'app-missing-products',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatSnackBarModule,
  ],
  templateUrl: './missing-products.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './missing-products.component.scss',
})
export class MissingProductsComponent implements OnInit {
  data = inject<MissingProductsSheetData>(MAT_BOTTOM_SHEET_DATA)
  private bottomSheetRef =
    inject<MatBottomSheetRef<MissingProductsComponent>>(MatBottomSheetRef)
  private reportService = inject(ReportService)
  private snackBar = inject(MatSnackBar)
  translationService = inject(TranslationService)

  loading = false
  hydrating = false
  error: string | null = null
  summary: MissingProductsSummary | null = null

  ngOnInit(): void {
    void this.loadMissingProducts()
  }

  private async loadMissingProducts(): Promise<void> {
    this.loading = true
    this.error = null

    try {
      const summary = await firstValueFrom(
        this.reportService.getMissingProducts(this.data.reportId),
      )
      this.summary = summary
    } catch (err) {
      console.error('Failed to load missing products', err)
      this.error =
        'Something went wrong while loading missing products. Please try again.'
    } finally {
      this.loading = false
    }
  }

  async onImportMissing(): Promise<void> {
    if (!this.summary || this.summary.missingCount === 0 || this.hydrating) {
      return
    }

    this.hydrating = true
    this.error = null

    try {
      await firstValueFrom(
        this.reportService.hydrateMissingProducts(this.data.reportId),
      )
      const importedCount = this.summary?.missingCount ?? 0
      this.snackBar.open(
        `Added ${importedCount} missing product(s) to this report.`,
        '',
        { duration: 5000 },
      )
      this.bottomSheetRef.dismiss({ hydrated: true })
    } catch (err) {
      console.error('Failed to import missing products', err)
      this.error =
        'Something went wrong while adding missing products. Please try again.'
    } finally {
      this.hydrating = false
    }
  }

  onClose(): void {
    this.bottomSheetRef.dismiss()
  }

  get hasMissing(): boolean {
    return !!this.summary && this.summary.missingCount > 0
  }
}
